import {UI} from "../../stem-core/src/ui/UIBase";
import {registerStyle} from "../../stem-core/src/ui/style/Theme";
import {styleRule} from "../../stem-core/src/decorators/Style";
import {StyleSheet} from "../../stem-core/src/ui/Style";
import {MerchantLogo} from "../common/MerchantLogo.jsx";
import {PanelCloseButton} from "./panel-elements/PanelCloseButton.jsx";
import {iFrameState} from "../services/IFrameState.js";
import {ScrollableBody} from "./ScrollableBody.jsx";
import {iFrameConnection} from "../services/IFrameConnection.js";
import {IFrameMessages} from "../../blink-sdk/messaging/IFrameMessages.js";
import {isSmallScreen} from "../UtilsLib.js";
import {Router} from "../../stem-core/src/ui/Router.jsx";
import {StepIndicator} from "../ui/StepIndicator.jsx";
import {GATE_TYPE} from "../../blink-sdk/Constants.js";
import {ADDRESS_PANEL_URL, INLINE_SUBSCRIPTION_URLS, PAYMENT_DETAILS_PANEL_URL} from "./PanelConstants.js";
import {LOGIN_URL, LOGIN_WITH_EMAIL_CODE_URL} from "../Constants.js";
import {delayDebounced} from "../../blink-sdk/utils/Utils.js";


export class BasePanelPageStyle extends StyleSheet {
    @styleRule
    container = {
        fontFamily: this.themeProps.MERCHANT_FONT_FAMILY,
        color: this.themeProps.MERCHANT_TEXT_PRIMARY_COLOR,
        overflow: "auto",
        maxWidth: "100%",
        maxHeight: "100%",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        padding: isSmallScreen() ? "18px 12px": null,
    };

    @styleRule
    header = {
        width: "100%",
        position: "sticky",
        top: 0,
        padding: 12,
        background: this.themeProps.MERCHANT_FRAME_HEADER_BACKGROUND,
        borderBottom: () => "0.5px solid " + this.themeProps.MERCHANT_FRAME_HEADER_BORDER_COLOR,
        zIndex: 3,
    };

    @styleRule
    body = {
        background: this.themeProps.MERCHANT_FRAME_BACKGROUND,
        border: this.themeProps.MERCHANT_FRAME_BORDER,
        width: "100%",
        overflow: "auto",
        position: "relative"
    };

    @styleRule
    content = {
        width: 440,
        maxWidth: "100%",
        padding: isSmallScreen() ? "12px 24px 12px 24px" : "18px 36px 18px 36px",
        overflow: "auto",
    }

    @styleRule
    title = () => ({
        color: this.themeProps.MERCHANT_TEXT_PRIMARY_COLOR,
        fontSize: this.themeProps.MERCHANT_FONT_SIZE_TITLE,
        fontWeight: this.themeProps.MERCHANT_FONT_WEIGHT_LIGHT,
        marginBottom: 12,
        ...this.themeProps.MERCHANT_TITLE_STYLE,
    });

    @styleRule
    message = {
        color: this.themeProps.MERCHANT_TEXT_SECONDARY_COLOR,
        textAlign: "center",
        width: "100%",
        margin: "18px 0px",
    }; // TODO @cleanup maybe this just needs to be PanelText whenever we use this?

    @styleRule
    actionButton = {
        width: "100%",
        margin: "auto",
        marginTop: 18,
        maxWidth: 440,
    }

    @styleRule
    backButton = {
        textAlign: "center",
        marginTop: 18,
        cursor: "pointer",
        display: "block",
        ":hover": {
            textDecoration: "none",
        }
    }
}

const STEPS = ["Account", "Address", "Payment"];

const URL_TO_STEP = {
    ["/" + LOGIN_URL]: "Account",
    ["/" + LOGIN_WITH_EMAIL_CODE_URL]: "Account",
    ["/" + ADDRESS_PANEL_URL]: "Address",
    ["/" + INLINE_SUBSCRIPTION_URLS.subscriptionCheckout]: "Payment",
    ["/" + PAYMENT_DETAILS_PANEL_URL]: "Payment",
    ["/" + INLINE_SUBSCRIPTION_URLS.subscribeThankYou]: null
}

@registerStyle(BasePanelPageStyle)
export class BasePanelPage extends UI.Element {
    getDefaultOptions() {
        const {iframeParams} = iFrameState;

        return {
            hasHeader: !iframeParams.inlineFlow,
            hasCloseButton: !iframeParams.disableUserClose,
            hasStepIndicator: iframeParams.hasStepIndicator ?? iframeParams.inlineFlow,
            hasShadow: true,
            shownInIframe: true,
        };
    }

    shouldDisplayHeader() {
        return this.options.hasHeader;
    }

    shouldDisplayCloseButton() {
        return this.options.hasCloseButton;
    }

    shouldDisplayShadow() {
        return this.options.hasShadow && this.shouldDisplayHeader();
    }

    shouldDisplayStepIndicator() {
        return this.options.hasStepIndicator && Router.getCurrentPath() in URL_TO_STEP;
    }

    renderStepIndicator() {
        return this.shouldDisplayStepIndicator() &&
            <StepIndicator
                steps={STEPS}
                activeStep={URL_TO_STEP[Router.getCurrentPath()]}
            />;
    }

    renderHeader() {
        const {styleSheet} = this;

        return this.shouldDisplayHeader() && [
            <div className={styleSheet.header}>
                <MerchantLogo />
                {this.shouldDisplayCloseButton() && <PanelCloseButton/>}
            </div>
        ];
    }

    getChildrenToRender() {
        const {styleSheet} = this;

        return [
            this.renderStepIndicator(),
            this.renderHeader(),
            <ScrollableBody className={styleSheet.body} hasShadow={this.shouldDisplayShadow()}>
                <div className={styleSheet.content}>
                    {this.options.title && <div className={styleSheet.title}>{this.options.title}</div>}
                    {this.render()}
                </div>
            </ScrollableBody>
        ]
    }

    getDispatchedHeight() {
        if (iFrameState.gateType === null ||
                iFrameState.gateType === GATE_TYPE.banner ||
                iFrameState.iframeParams.inlineFlow) {
            return this.node?.offsetHeight;
        }
        return "100%";
    }

    getDispatchedWidth() {
        return "100%";
    }

    dispatchSizeChange() {
        const height = this.getDispatchedHeight();
        const width = this.getDispatchedWidth();

        if (height && width && (height !== this.lastDispatchedHeight || width !== this.lastDispatchedWidth)) {
            this.lastDispatchedHeight = height;
            this.lastDispatchedWidth = width;
            iFrameConnection.sendToSDK(IFrameMessages.UPDATE_PANEL_SIZE, {height, width});
        }
    }

    onMount() {
        if (this.options.shownInIframe) {
            setTimeout(() => {
                if (!this.node) {
                    // TODO this seems to happen, not sure why
                    return;
                }

                const throttledSizeChange = delayDebounced(() => this.dispatchSizeChange(), 100);

                // TODO Consider adding a resize observer function or a method attachResizeListener(node, callback)
                if (self.ResizeObserver) {
                    const observer = new ResizeObserver(throttledSizeChange);
                    observer.observe(this.node);
                }

                this.dispatchSizeChange();
            });
        }
    }
}
